import React, { useState } from "react";
import { Preferences } from "@capacitor/preferences";

// Create a simple event emitter
class EventEmitter {
  constructor() {
    this.events = {};
  }

  on(event, callback) {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event].push(callback);
    return () => this.remove(event, callback);
  }

  remove(event, callback) {
    if (!this.events[event]) return;
    this.events[event] = this.events[event].filter(cb => cb !== callback);
  }

  emit(event, data) {
    if (!this.events[event]) return;
    this.events[event].forEach(callback => callback(data));
  }

  removeAll(event) {
    if (event) {
      delete this.events[event];
    } else {
      this.events = {};
    }
  }
}

// Create a single instance of EventEmitter
export const preferencesEmitter = new EventEmitter();

export { Preferences };

export const saveFcmToken = async (item) => {
  await Preferences.set({
    key: "fcm_token",
    value: item,
  });
};

export const loadFcmToken = async () => {
  const { value } = await Preferences.get({ key: "fcm_token" });
  if (value != null) {
    // console.log("fcm_token: ", value);
  }
  return value;
};

export const deleteFcmToken = async () => {
  await Preferences.remove({ key: "fcm_token" });
};

export const saveToken = async (item) => {
  await Preferences.set({
    key: "token",
    value: item,
  });
};

export const loadToken = async () => {
  const { value } = await Preferences.get({ key: "token" });
  // if (value != null) {
  //   console.log(value);
  // }
  return value;
};

export const deleteToken = async () => {
  await Preferences.remove({ key: "token" });
  await Preferences.remove({ key: "user" });
};

export const saveUser = async (item) => {
  await Preferences.set({
    key: "user",
    value: JSON.stringify(item),
  });
  preferencesEmitter.emit("userUpdated", item);
};

export const loadUser = async () => {
  try {
    const { value } = await Preferences.get({ key: "user" });
    if (!value) {
      return null;
    }

    // Try to parse as JSON
    try {
      const parsed = JSON.parse(value);
      return parsed;
    } catch (parseError) {
      // If parsing fails, the data might be corrupted or invalid
      console.error("Error parsing user data:", parseError);
      console.error("Raw value:", value);

      // Clear corrupted data
      await Preferences.remove({ key: "user" });
      return null;
    }
  } catch (error) {
    console.error("Error loading user data:", error);
    return null;
  }
};

export const loadGlobalData = async () => {
  const { value } = await Preferences.get({ key: "global_data" });
  return value;
};

export const saveGlobalData = async (data) => {
  const value = typeof data === "string" ? data : JSON.stringify(data);

  await Preferences.set({
    key: "global_data",
    value,
  });
};

export const deleteUser = async () => {
  await Preferences.remove({ key: "user" });
};

//? referral_code
export const saveReferralCode = async (item) => {
  await Preferences.set({
    key: "referral_code",
    value: item,
  });
};

export const loadReferralCode = async () => {
  const { value } = await Preferences.get({ key: "referral_code" });
  return value && value !== "null" && value !== "undefined" ? value : "";
};

export const deleteReferralCode = async () => {
  await Preferences.remove({ key: "referral_code" });
};

//? refer_from
export const saveReferFrom = async (item) => {
  await Preferences.set({
    key: "refer_from",
    value: item,
  });
};

export const loadReferFrom = async () => {
  const { value } = await Preferences.get({ key: "refer_from" });
  return value;
};

export const deleteReferFrom = async () => {
  await Preferences.remove({ key: "refer_from" });
};

//? navigate data
export const getNavigateData = async () => {
  const { value } = await Preferences.get({ key: "navigate_data" });
  return value ? JSON.parse(value) : {};
};

export const setNavigateData = async (obj) => {
  const existing = await getNavigateData();
  const merged = {
    ...existing,
    ...obj,
  };
  await Preferences.set({
    key: "navigate_data",
    value: JSON.stringify(merged),
  });
};

export const removeNavigateDataKey = async (key) => {
  const { value } = await Preferences.get({ key: "navigate_data" });
  if (!value) return;

  const data = JSON.parse(value);
  if (key in data) {
    delete data[key];
    await Preferences.set({
      key: "navigate_data",
      value: JSON.stringify(data),
    });
  }
};

//? home popup image last show
export const setLastImageShown = async (timestamp) => {
  await Preferences.set({
    key: "last_image_shown",
    value: String(timestamp),
  });
};

export const getLastImageShown = async () => {
  const { value } = await Preferences.get({ key: "last_image_shown" });
  return value;
};

//? in game detail
export const getUserInGameDetail = async () => {
  const { value } = await Preferences.get({ key: "userInGameDetail" });
  return value ? JSON.parse(value) : {};
};

export const setUserInGameDetail = async (item) => {
  await Preferences.set({
    key: "userInGameDetail",
    value: JSON.stringify(item),
  });
};

export const deleteUserInGameDetail = async () => {
  await Preferences.remove({ key: "userInGameDetail" });
};

//? chat room channel
export const setChatroomChannel = async (item) => {
  await Preferences.set({
    key: "chatroom_channel",
    value: item,
  });
};

export const getChatroomChannel = async () => {
  const { value } = await Preferences.get({ key: "chatroom_channel" });
  return value;
};

//? userRef (sync — web/localStorage)
export const getUserRefs = () => {
  return JSON.parse(localStorage.getItem("userRefs") || "[]");
};

export const setUserRefs = (refs) => {
  localStorage.setItem("userRefs", JSON.stringify(refs));
};

export const checkandAddUserRef = (ref) => {
  const refs = getUserRefs();
  if (!refs.includes(ref)) {
    refs.push(ref);
    setUserRefs(refs);
    return true;
  }
  return false;
};

//? logo image
export const setLogoImage = async (logoLink) => {
  await Preferences.set({
    key: "logo_image",
    value: String(logoLink),
  });
};

export const getLogoImage = async () => {
  const { value } = await Preferences.get({ key: "logo_image" });
  return value;
};

const TokenManager = () => {
  const [token, setTokenState] = useState("");

  const loadToken = async () => {
    const { value } = await Preferences.get({ key: "token" });
    setTokenState(value); // Set state to display the token
  };

  const deleteToken = async () => {
    await Preferences.remove({ key: "token" });
    setTokenState(""); // Clear token from state
  };
};

export const setLanguage = async (item) => {
  await Preferences.set({
    key: "lng",
    value: item,
  });
  preferencesEmitter.emit("languageChanged", item);
};

export const loadLanguage = async () => {
  const { value } = await Preferences.get({ key: "lng" });
  return value ?? null;
};

/** Alias for {@link loadLanguage} (older call sites). */
export const getLanguage = loadLanguage;

export default TokenManager;
